import { supabase } from '../config/supabase';

export interface RecipeAnalysisRequest {
  text?: string;
  imageBase64?: string;
  imageType?: string;
}

export interface RecipeAnalysisResponse {
  name: string;
  description: string;
  prepTime: number;
  cookTime: number;
  portionSize: number;
  categories: string[];
  ingredients: Array<{
    name: string;
    amount: number;
    unit: string;
  }>;
  steps: Array<{
    step: number;
    instructions: string;
  }>;
  missingFields: string[];
}

export interface AIAnalysisResult {
  success: boolean;
  recipe?: RecipeAnalysisResponse;
  error?: string;
}

export const analyzeRecipe = async (request: RecipeAnalysisRequest): Promise<AIAnalysisResult> => {
  try {
    const payload = {
      text: request.text || '',
      imageBase64: request.imageBase64 || '',
      imageType: request.imageType || ''
    };

    // Log the payload for debugging
    console.log('🔍 AI Analysis Request Payload:', {
      hasText: !!payload.text,
      textLength: payload.text.length,
      hasImage: !!payload.imageBase64,
      imageLength: payload.imageBase64.length,
      imageType: payload.imageType,
      fullPayload: payload
    });

    // Use Supabase client with proper authentication
    const { data, error } = await supabase.functions.invoke('analyze-recipe', {
      body: payload,
    });

    if (error) {
      console.error('❌ AI Analysis Supabase Error:', error);
      return {
        success: false,
        error: `Edge Function Error: ${error.message}`
      };
    }

    if (!data?.success) {
      console.error('❌ AI Analysis Function Error:', data);
      return {
        success: false,
        error: data?.error || 'Unknown error occurred'
      };
    }

    console.log('✅ AI Analysis Success Response:', data);
    return {
      success: true,
      recipe: data.recipe,
    };
  } catch (error) {
    console.error('❌ AI Analysis Network Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error'
    };
  }
};

// Utility function to compress image if needed
export const compressImage = async (
  file: File,
  maxSizeMB: number = 5
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        // Calculate new dimensions to maintain aspect ratio
        let { width, height } = img;
        const maxSize = maxSizeMB * 1024 * 1024; // Convert to bytes
        
        // If image is already small enough, return as is
        if (file.size <= maxSize) {
          resolve(e.target?.result as string);
          return;
        }

        // Calculate compression ratio
        const compressionRatio = Math.sqrt(maxSize / file.size);
        width *= compressionRatio;
        height *= compressionRatio;

        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to base64 with compression
        const compressedBase64 = canvas.toDataURL('image/jpeg', 0.8);
        resolve(compressedBase64);
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}; 